import store from '@/store'
import AcceptanceService from '@/services/AcceptanceService'
import {
  AcceptanceCheckNavigation,
  decideAcceptanceCheck
} from '@/helpers/acceptanceCheckTiming'

// NWS_SMILELINE-1964: 「同意待ち」はこれまで localStorage の印（needs_acceptance）だけで判定しており、
// 印はログインのときにしか付かない。そのため、規約の同意機能のリリース（2026-05-21）より前から
// ログインしたままの人や、別タブのログイン画面で印が消えた人は、同意画面が一度も出ずに使えていた。
//
// ページを開いた（開き直した・再読み込みした）あと、ログインした状態で最初に中の画面へ進むときに 1 回だけ、
// GET /agreement でサーバー側の実際の同意状態を確かめて印を合わせる。印が付けば、
// 続く router.beforeEach の判定で同意画面へ送られる。
// 最初に開いた画面がログイン不要の画面（招待リンクのプレビューなど）でも、そのあと中へ進むときに確かめる。
// 決定事項（2026-10-09）「利用中のユーザーに即時には同意画面を出さない」を守るため、
// それより後のページ内の遷移では呼ばない。タブの切り替え・通信の失敗でも確かめ直さない（2026-11-02 natomi）。
let checkedOnPageLoad = false

// 確認の通信が返らないときに、最初の画面がいつまでも開かないのを避ける
const TIMEOUT_MS = 10000

/** この遷移でサーバーに確かめるかを返す。確かめる遷移が来たら、以後は false にする */
export function takePageLoadCheck(nav: AcceptanceCheckNavigation): boolean {
  const decision = decideAcceptanceCheck(checkedOnPageLoad, nav)
  if (decision.markChecked) checkedOnPageLoad = true
  return decision.callServer
}

/**
 * サーバーの同意状態で localStorage の印を合わせる。
 * 通信の失敗・タイムアウトのときは印を変えない（今までどおり印で判定する）。
 * 10 秒の区切りは、トークンが切れていたときの更新（interceptor の refreshAccessToken。こちらは区切りが無い）を含めた
 * 確認全体に掛ける。区切りの後に遅れて返った結果では印を変えない（使い始めた後に同意画面へ切り替わらないように）。
 */
export async function syncAcceptanceFromServer(): Promise<void> {
  if (!store.state.auth.token) return
  let timedOut = false
  let timer: ReturnType<typeof setTimeout> | undefined
  const check = (async () => {
    try {
      const response = await AcceptanceService.getAcceptance({ timeout: TIMEOUT_MS })
      if (timedOut) return
      const status = response?.data?.status
      if (!status || typeof status.needs_acceptance !== 'boolean') return
      if (status.needs_acceptance) {
        localStorage.setItem('needs_acceptance', 'true')
      } else {
        localStorage.removeItem('needs_acceptance')
      }
    } catch (error) {
      console.log(error)
    }
  })()
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(() => {
      timedOut = true
      resolve()
    }, TIMEOUT_MS)
  })
  try {
    await Promise.race([check, timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}
